import React from 'react';
import { ShieldAlert, Info, MapPin, FileText, Users, Calendar, DollarSign } from 'lucide-react';

const ComplianceContent = () => {
    // Palette Colors Reference:
    // Dark Text: #1F2A33
    // Brand Accent: #E84C5B
    // Light Backgrounds: #F6F4F1
    // Border/Muted: #D6D1CB

    const locations = [
        {
            name: "East Hampton (Town)",
            permit: "Not required; property must be registered.",
            fee: "$100, valid for 2 years.",
            minStay: "14 consecutive nights (Exception: 2 rentals < 14 nights allowed per 6 months).",
            occupancy: "Max 4 unrelated persons.",
            notes: "Registration number must be included in advertisements."
        },
        {
            name: "East Hampton Village",
            permit: "Not required.",
            fee: "None.",
            minStay: "30 consecutive nights (Exception: 2 rentals of 14 nights allowed per calendar year).",
            occupancy: "Max 3 unrelated persons (unless single housekeeping unit).",
            notes: "Occupancy beyond limit requires ZBA approval."
        },
        {
            name: "North Haven",
            permit: "Partially required (May 1 – Sept 30).",
            fee: "$250 per rental season.",
            minStay: "14 consecutive nights.",
            occupancy: "Max 6 unrelated persons (unless single housekeeping unit).",
            notes: "Occupancy beyond limit requires ZBA approval."
        },
        {
            name: "Quogue",
            permit: "Partially required (May 1 – Sept 30).",
            fee: "$125 (14-31 nights) / $250 (31+ nights). New application per tenancy.",
            minStay: "14 consecutive nights.",
            occupancy: "Max 6 unrelated persons (max 2 per bedroom).",
            notes: "Occupancy beyond limit requires ZBA approval."
        },
        {
            name: "Southampton Town",
            permit: "Required.",
            fee: "$200, valid for 2 years.",
            minStay: "14 consecutive nights.",
            occupancy: "Max 3 unrelated persons (unless functional equivalent of a family).",
            notes: "NYS regulations apply based on bedroom count/size."
        },
        {
            name: "Southampton Village",
            permit: "Required.",
            fee: "$200, valid for 2 years.",
            minStay: "14 consecutive nights.",
            occupancy: "Max 3 unrelated persons (unless functional equivalent of a family).",
            notes: "NYS regulations apply based on bedroom count/size."
        },
        {
            name: "West Hampton Beach",
            permit: "Partially required (May 1 – Sept 30).",
            fee: "$150 (< 30 days) / $250 (30+ days). New application per tenancy.",
            minStay: "No minimum stay requirement.",
            occupancy: "Max 6 unrelated persons (unless single housekeeping unit).",
            notes: "Occupancy beyond limit requires ZBA approval."
        }
    ];

    return (
        <div className="pt-32 pb-24 px-6 max-w-7xl mx-auto animate-fade-in font-sans text-[#1F2A33]">

            {/* Header */}
            <header className="mb-16 text-center max-w-4xl mx-auto border-b border-[#D6D1CB] pb-10">
                <span className="text-[#E84C5B] font-bold tracking-widest uppercase text-sm mb-4 block">
                    Local Regulations
                </span>
                <h1 className="text-4xl md:text-6xl font-serif font-bold text-[#1F2A33] mb-6 leading-tight">
                    Compliance & Safety
                </h1>
                <p className="text-lg text-gray-500 max-w-2xl mx-auto">
                    We are committed to operating in full compliance with local laws. Below is an overview of the rental regulations for our key destinations.
                </p>
            </header>

            {/* General Disclaimer Section */}
            <section className="bg-[#F6F4F1] rounded-[2.5rem] p-8 md:p-12 mb-20 border border-[#D6D1CB] relative overflow-hidden">
                <div className="absolute top-0 left-0 w-2 h-full bg-[#E84C5B]"></div>
                <div className="flex items-start gap-6">
                    <div className="hidden md:block p-4 bg-white rounded-full shadow-sm text-[#E84C5B]">
                        <ShieldAlert className="w-8 h-8" />
                    </div>
                    <div className="space-y-4 text-[#1F2A33]/90 leading-relaxed">
                        <h2 className="text-2xl font-serif font-bold text-[#1F2A33] mb-2">Know the Local Regulations</h2>
                        <p>
                            The information on this page is provided for general informational purposes only and does not constitute legal, regulatory, tax, or professional advice. AnotherHome does not provide legal advice. Laws and regulations vary by jurisdiction and are subject to change.
                            Reservations made through AnotherHome are subject to review and confirmation by the applicable property owner and/or AnotherHome. Availability, listing status, length of stay, registration or permitting requirements, and acceptance of reservations may be affected by property-specific requirements and applicable laws. AnotherHome does not represent or warrant that any property or reservation is legally eligible for rental.
                            <br />
                            Certain municipalities impose minimum stay requirements (including 14-night minimums), as well as registration or licensing obligations, advertising restrictions, occupancy limits, and tax requirements. Non-compliance may result in fines, penalties, civil liability, unenforceable rental agreements, or limitations on the ability to collect rent or enforce agreements.
                            <br />
                            Property owners and guests are responsible for independently verifying all applicable legal, regulatory, and tax requirements with appropriate authorities or qualified professionals before listing, booking, or occupying a property.
                        </p>
                        <p>
                            <strong>Another Home</strong> encourages property owners and guests to independently verify applicable legal, regulatory, and tax requirements with relevant local authorities and/or qualified legal or tax professionals before making decisions based on this information.
                        </p>
                        <p>
                            Many municipalities have enacted laws governing the rental of residential properties. These laws may affect the legal right to rent a property, permitted rental durations, registration or licensing requirements, and the ability to enforce rental agreements or collect rent through legal proceedings.
                        </p>
                        <p className="text-sm italic opacity-80 pt-4 border-t border-[#D6D1CB]/50">
                            Failure to comply with applicable regulations may result in fines, penalties, or adverse outcomes in civil proceedings. Another Home and its representatives do not provide legal advice. For guidance specific to your situation, please consult the relevant local authorities.
                        </p>
                    </div>
                </div>
            </section>

            {/* Detailed Locations Grid */}
            <section>
                <div className="flex items-center gap-3 mb-10">
                    <MapPin className="w-6 h-6 text-[#E84C5B]" />
                    <h2 className="text-3xl font-serif font-bold text-[#1F2A33]">Municipality Summaries</h2>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {locations.map((loc, index) => (
                        <div key={index} className="bg-white border border-[#D6D1CB] rounded-3xl p-8 hover:shadow-xl transition-all duration-300 group">
                            <h3 className="text-2xl font-bold font-serif text-[#1F2A33] mb-6 pb-4 border-b border-[#D6D1CB]/30 group-hover:text-[#E84C5B] transition-colors">
                                {loc.name}
                            </h3>

                            <div className="space-y-5">
                                {/* Permit */}
                                <div className="flex gap-4">
                                    <FileText className="w-5 h-5 text-[#E84C5B] shrink-0 mt-1" />
                                    <div>
                                        <span className="block text-xs font-bold uppercase text-gray-400 mb-1">Permit Requirement</span>
                                        <p className="text-sm md:text-base font-medium text-gray-700">{loc.permit}</p>
                                    </div>
                                </div>

                                {/* Fee */}
                                <div className="flex gap-4">
                                    <DollarSign className="w-5 h-5 text-[#E84C5B] shrink-0 mt-1" />
                                    <div>
                                        <span className="block text-xs font-bold uppercase text-gray-400 mb-1">Registration Fee</span>
                                        <p className="text-sm md:text-base font-medium text-gray-700">{loc.fee}</p>
                                    </div>
                                </div>

                                {/* Min Stay */}
                                <div className="flex gap-4">
                                    <Calendar className="w-5 h-5 text-[#E84C5B] shrink-0 mt-1" />
                                    <div>
                                        <span className="block text-xs font-bold uppercase text-gray-400 mb-1">Min. Length of Stay</span>
                                        <p className="text-sm md:text-base font-medium text-gray-700">{loc.minStay}</p>
                                    </div>
                                </div>

                                {/* Occupancy */}
                                <div className="flex gap-4">
                                    <Users className="w-5 h-5 text-[#E84C5B] shrink-0 mt-1" />
                                    <div>
                                        <span className="block text-xs font-bold uppercase text-gray-400 mb-1">Max Occupancy</span>
                                        <p className="text-sm md:text-base font-medium text-gray-700">{loc.occupancy}</p>
                                    </div>
                                </div>

                                {/* Notes */}
                                {loc.notes && (
                                    <div className="bg-[#F6F4F1] p-4 rounded-xl mt-4 flex gap-3 text-xs md:text-sm text-gray-600">
                                        <Info className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                                        {loc.notes}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Footer Note */}
            <div className="mt-16 text-center">
                <p className="text-gray-400 text-sm">
                    Information regarding tax requirements is forthcoming. Please check back for updates.
                </p>
            </div>
        </div>
    );
};

export default ComplianceContent;